function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { objectKeys } from '../../utils';
import * as React from 'react';
import { Toast } from './toast';
import { SlideFade } from '../transition';
import { TransitionGroup } from 'react-transition-group';
import { findToast, getToastPosition } from './toast.utils';
/**
 * Id counter to create unique ids
 * across all corners ("top", "bottom", etc.)
 */
var ToastCounter = 0;

/**
 * Manages the creation, and removal of toasts
 * across all corners ("top", "bottom", etc.)
 */
export class ToastManager extends React.Component {
  constructor(props) {
    var _this;
    super(props);
    _this = this;
    _defineProperty(this, "notify", (message, options) => {
      var toast = this.createToast(message, options);
      var {
        position,
        id
      } = toast;
      this.setState(prevToasts => {
        var isTop = position.includes('top');

        /**
         * - If the toast is positioned at the top edges, the
         * recent toast stacks on top of the other toasts.
         *
         * - If the toast is positioned at the bottom edges, the recent
         * toast stacks below the other toasts.
         */
        var toasts = isTop ? [toast, ...prevToasts[position]] : [...prevToasts[position], toast];
        return _objectSpread(_objectSpread({}, prevToasts), {}, {
          [position]: toasts
        });
      });
      return id;
    });
    _defineProperty(this, "updateToast", (id, options) => {
      this.setState(prevState => {
        var nextState = _objectSpread({}, prevState);
        var {
          position,
          index
        } = findToast(nextState, id);
        if (position && index !== -1) {
          nextState[position][index] = _objectSpread(_objectSpread({}, nextState[position][index]), options);
        }
        return nextState;
      });
    });
    _defineProperty(this, "closeAll", function () {
      var {
        positions
      } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      // only one setState here for perf reasons
      // instead of spamming this.closeToast
      _this.setState(prev => {
        var allPositions = ['bottom', 'bottom-right', 'bottom-left', 'top', 'top-left', 'top-right'];
        var positionsToClose = positions !== null && positions !== void 0 ? positions : allPositions;
        return positionsToClose.reduce((acc, position) => {
          acc[position] = prev[position].map(toast => _objectSpread(_objectSpread({}, toast), {}, {
            requestClose: true
          }));
          return acc;
        }, {});
      });
    });
    _defineProperty(this, "createToast", (message, options) => {
      var _options$id, _options$position;
      ToastCounter += 1;
      var id = (_options$id = options.id) !== null && _options$id !== void 0 ? _options$id : ToastCounter;
      var position = (_options$position = options.position) !== null && _options$position !== void 0 ? _options$position : 'top';
      return {
        id,
        message,
        position,
        duration: options.duration,
        onCloseComplete: options.onCloseComplete,
        onRequestRemove: () => this.removeToast(String(id), position),
        status: options.status,
        requestClose: false
      };
    });
    _defineProperty(this, "closeToast", id => {
      this.setState(prevState => {
        var position = getToastPosition(prevState, id);
        if (!position) return prevState;
        return _objectSpread(_objectSpread({}, prevState), {}, {
          [position]: prevState[position].map(toast => {
            // id may be string or number
            // eslint-disable-next-line eqeqeq
            if (toast.id == id) {
              return _objectSpread(_objectSpread({}, toast), {}, {
                requestClose: true
              });
            }
            return toast;
          })
        });
      });
    });
    _defineProperty(this, "removeToast", (id, position) => {
      this.setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
        [position]: prevState[position].filter(toast => toast.id != id)
      }));
    });
    _defineProperty(this, "isVisible", id => {
      var {
        position
      } = findToast(this.state, id);
      return Boolean(position);
    });
    _defineProperty(this, "getStyle", position => {
      var isTopOrBottom = position === 'top' || position === 'bottom';
      var margin = isTopOrBottom ? '0 auto' : undefined;
      var top = position.includes('top') ? 0 : undefined;
      var bottom = position.includes('bottom') ? 0 : undefined;
      var right = !position.includes('left') ? 0 : undefined;
      var left = !position.includes('right') ? 0 : undefined;
      return {
        position: 'fixed',
        zIndex: 5500,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        listStyle: 'none',
        margin,
        top,
        bottom,
        right,
        left
      };
    });
    _defineProperty(this, "getToastStartingOffsetValues", position => {
      var offsetX = 0;
      var offsetY = 0;
      if (['top-left', 'bottom-left'].includes(position)) {
        offsetX = '-100%';
      } else if (['top-right', 'bottom-right'].includes(position)) {
        offsetX = '100%';
      }
      if (position === 'top') {
        offsetY = '-100%';
      } else if (position === 'bottom') {
        offsetY = '100%';
      }
      return {
        offsetX,
        offsetY
      };
    });
    this.state = {
      top: [],
      'top-left': [],
      'top-right': [],
      'bottom-left': [],
      bottom: [],
      'bottom-right': []
    };
    var methods = {
      notify: this.notify,
      closeAll: this.closeAll,
      close: this.closeToast,
      update: this.updateToast,
      isActive: this.isVisible
    };
    props.notify(methods);
  }

  /**
   * Function to actually create a toast and add it
   * to state at the specified position
   */

  render() {
    return objectKeys(this.state).map(position => {
      var toasts = this.state[position];
      return /*#__PURE__*/React.createElement("ul", {
        key: position,
        id: "tqc-toast-manager-".concat(position),
        style: this.getStyle(position)
      }, /*#__PURE__*/React.createElement(TransitionGroup, {
        component: "li"
      }, toasts.map(toast => {
        var toastOffsets = this.getToastStartingOffsetValues(position);
        return /*#__PURE__*/React.createElement(SlideFade, _extends({
          key: toast.id
        }, toastOffsets, {
          onExited: toast.onCloseComplete
        }), /*#__PURE__*/React.createElement(Toast, toast));
      })));
    });
  }
}